import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { RoleSummary, RoleSummaryStore, StructmasterUserRoleName } from 'src/stores/roles-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { Card, Row, Tag } from 'antd';
import { Query } from 'src/core/stores/data-store';
import { formatMessage } from 'src/core/services/http.service';
import { useIdentity } from 'src/core/services/authentication';
import { ENV } from 'src/';
import { emptyTableModel, getRoleDisplayName } from 'src/common/utils';
import { useNavigate } from 'react-router-dom';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { roleIsLocked } from 'src/stores/default-roles-store';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { OptionsFilter } from 'src/common/components/table-filters/options-filter';
import { Selector } from 'src/common/types';
import { guid } from 'odata-query';

const AdminRolesListPage: FC = () => {
  const identity = useIdentity();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config: AppConfiguration = useContext(AppConfig);

  const organizationId = identity.profile['tenant_id'];
  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'name', direction: 'Ascending', useProfile: false }],
    skip: 0,
    take: 10,
    filter: { OrganizationId: guid(organizationId) },
  } as Query);

  const rolesStore = container.get(RoleSummaryStore);
  const rolesState = rolesStore.state;

  setHeader(t('AdminRoles'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'adminroles', breadcrumbName: t('AdminRoles') },
  ]);

  useEffect(
    () => {
      rolesStore.load(query);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const tableModel = {
    query: query,
    rowDisabled: (data) => data.name === StructmasterUserRoleName,
    columns: [
      {
        sortable: true,
        searchable: true,
        field: 'name',
        title: t('Name'),
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter
            baseUrl={rolesStore.baseUrl}
            fieldName={'Name'}
            fieldValue={'name'}
            filter={`organizationId eq ${organizationId}`}
          />
        ),
        fiterField: 'Name',
        renderer: (data) => <span className={roleIsLocked(data.name) ? 'hide-checkbox' : ''}>{getRoleDisplayName(data.name)}</span>,
      },
      {
        sortable: true,
        field: 'selector',
        title: t('Selector'),
        filter: <OptionsFilter options={Object.entries(Selector).map(([key, value]) => ({ label: t(key), value: value }))} />,
        renderer: (data) => (
          <span>
            <Tag>{t(data.selector)}</Tag>
          </span>
        ),
      },
      {
        renderer: (data: RoleSummary) =>
          !roleIsLocked(data.name) && (
            <span className="clickable-span strong" onClick={() => navigate(`update/${data.id}`, { state: data })}>
              {t('Edit')}
            </span>
          ),
      },
    ],
    data: rolesState.value,
  } as TableModel<RoleSummary>;

  return (
    <Row align="top" justify="space-between">
      <Card className="card-table" style={{ width: '100%' }}>
        <TableView
          rowKey="id"
          canCreateNew
          onNewItem={() => navigate(`new`)}
          newItemName={t('CreateRole')}
          canDelete
          onDeleteRow={(item: RoleSummary) => (!roleIsLocked(item.name) ? rolesStore.delete(item.id) : undefined)}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            setQuery(query);
            rolesStore.load(query);
          }}
          onRefresh={() => rolesStore.load(query)}
          model={rolesState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            rolesState.errorMessage.value &&
            (config.environment === ENV.DEV ? formatMessage(rolesState.errorMessage.value) : t('AdminRolesTableError'))
          }
        />
      </Card>
    </Row>
  );
};

export default AdminRolesListPage;
